import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import { Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { theme } from '@/styles/theme';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { SceneCanvas } from './SceneCanvas';
import { useHeaderMode } from '@/context/HeaderModeContext';
import harmoniePhoto from '@/assets/images/Foto Harmonie.jpg';

/**
 * CinematicHero - the home hero: the real ensemble photo, centred, sunk under
 * a navy veil, with a softened pass of the welcome screen's silk ribbons
 * glowing over it (the canvas is transparent and additive, so it composites
 * straight onto the photo). The copy stays left-anchored against the photo's
 * centre (asymmetric split, not a centred poster). Scroll progress dollies
 * the camera through the ribbons. Isolated from framer-motion (which only
 * drives the DOM overlay). Reports its visibility to the global Header so
 * the bar sits transparent over the hero and flips to solid past it.
 */

const HeroSection = styled.section`
  position: relative;
  /* svh, not dvh: dvh re-resolves while the mobile address bar collapses,
     which resizes the hero mid-scroll and makes the photo band below jump. */
  min-height: 100vh;
  min-height: 100svh;
  display: flex;
  align-items: center;
  overflow: hidden;
  background:
    radial-gradient(120% 80% at 72% 0%, ${theme.colors.primary.royalBlue} 0%, transparent 55%),
    ${theme.colors.primary.navy};

  /* Pull up under the (transparent) fixed header by cancelling the Main offset. */
  margin-top: calc(-1 * var(--kph-header-offset));
`;

/* Pinned navy backdrop behind the page: when iOS rubber-bands past the top,
   the document shifts down but this stays put, so the gap under the fixed
   header reads navy instead of the white body canvas. */
const OverscrollBleed = styled.div`
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background: ${theme.colors.primary.navy};
`;

/* The ensemble photo, centred, as the base layer of the hero. */
const PhotoLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
`;

const HeroPhoto = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 38%;
  filter: saturate(0.9);
`;

/* Navy veil sinking the photo into the brand field: heavier on the left
   where the copy sits, lighter centre-right where the ribbons glow. */
const PhotoVeil = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(100deg, rgba(16, 19, 31, 0.88) 0%, rgba(16, 19, 31, 0.66) 45%, rgba(16, 19, 31, 0.44) 100%),
    linear-gradient(180deg, rgba(16, 19, 31, 0.5) 0%, transparent 30%, transparent 62%, rgba(16, 19, 31, 0.82) 100%);
`;

const SceneLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  /* Softer ribbon pass: the photo carries the image now, the silk only glows. */
  opacity: 0.55;
`;

// WebGL-free fallback: static ribbon arcs in the section colours over navy,
// so the hero still echoes the welcome screen without a renderer.
const StaticBackdrop = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  background:
    radial-gradient(55% 45% at 68% 30%, rgba(212, 175, 55, 0.16) 0%, transparent 70%),
    radial-gradient(120% 90% at 50% 100%, ${theme.colors.primary.darkBlue} 0%, transparent 60%);
`;

const FallbackArc = styled.span`
  position: absolute;
  left: 68%;
  top: 42%;
  width: 70vmin;
  height: 70vmin;
  border-radius: 50%;
  transform-origin: center;
  filter: blur(2px);
  opacity: 0.5;
`;

// Light finishing scrim above the ribbons (the PhotoVeil below carries most
// of the darkening): steadies the text column and the top edge under the
// transparent header.
const Scrim = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  background:
    linear-gradient(100deg, rgba(16, 19, 31, 0.38) 0%, rgba(16, 19, 31, 0.16) 45%, transparent 70%),
    linear-gradient(180deg, rgba(16, 19, 31, 0.35) 0%, transparent 26%);
`;

const HeroContent = styled(Container)`
  position: relative;
  z-index: 4;
  color: ${theme.colors.neutral.white};
  padding-top: ${theme.spacing[24]};
  padding-bottom: ${theme.spacing[16]};
`;

// Left-anchored column; the ribbon wreath owns the right half of the frame.
const HeroBlock = styled.div`
  max-width: 760px;

  @media (max-width: ${theme.breakpoints.md}) {
    max-width: 100%;
  }
`;

const Eyebrow = styled(motion.span)`
  display: block;
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.sm};
  letter-spacing: 0.26em;
  text-transform: uppercase;
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.primary.lightGold};
  margin-bottom: ${theme.spacing[5]};

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.fontSizes.xs};
    letter-spacing: 0.2em;
  }
`;

const GoldRule = styled(motion.span)`
  display: block;
  width: 48px;
  height: 3px;
  background: ${theme.colors.primary.gold};
  margin-bottom: ${theme.spacing[6]};
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${theme.fonts.decorative};
  font-size: clamp(2.6rem, 6.5vw, 5.5rem);
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.neutral.white};
  margin-bottom: ${theme.spacing[6]};
  line-height: 1.1;
  letter-spacing: -0.01em;
  text-shadow: 0 6px 40px rgba(16, 19, 31, 0.55);

  em {
    font-style: italic;
    font-weight: ${theme.fontWeights.semibold};
    color: ${theme.colors.primary.gold};
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-family: ${theme.fonts.body};
  font-size: clamp(1.125rem, 2.4vw, 1.5rem);
  color: rgba(255, 255, 255, 0.88);
  margin: 0 0 ${theme.spacing[10]};
  max-width: 560px;
  line-height: ${theme.lineHeights.relaxed};
  font-weight: ${theme.fontWeights.medium};
  text-shadow: 0 2px 18px rgba(16, 19, 31, 0.5);
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const fade = (reduced: boolean, delay: number) =>
  reduced
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.3 } }
    : {
        initial: { opacity: 0, y: 26 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] as const },
      };

const Fallback = () => (
  <StaticBackdrop aria-hidden="true">
    <FallbackArc
      style={{
        transform: 'translate(-50%, -50%) rotate(-24deg)',
        border: '5px solid transparent',
        borderTopColor: 'rgba(212, 175, 55, 0.55)',
        borderRightColor: 'rgba(212, 175, 55, 0.18)',
      }}
    />
    <FallbackArc
      style={{
        transform: 'translate(-50%, -50%) rotate(48deg) scale(0.8)',
        border: '4px solid transparent',
        borderTopColor: 'rgba(184, 115, 51, 0.5)',
        borderLeftColor: 'rgba(184, 115, 51, 0.16)',
      }}
    />
    <FallbackArc
      style={{
        transform: 'translate(-50%, -50%) rotate(132deg) scale(1.14)',
        border: '3px solid transparent',
        borderTopColor: 'rgba(207, 207, 217, 0.45)',
        borderBottomColor: 'rgba(207, 207, 217, 0.14)',
      }}
    />
  </StaticBackdrop>
);

export const CinematicHero = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const reduced = useReducedMotion() ?? false;
  const { setOverHero } = useHeaderMode();

  // Tell the Header to go transparent while the hero fills the top of the
  // viewport, and solid once it scrolls one header-height past. No scroll
  // listener: a single IntersectionObserver flip.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setOverHero(entry?.isIntersecting ?? false),
      { rootMargin: '-72px 0px 0px 0px', threshold: 0 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      setOverHero(false);
    };
  }, [setOverHero]);

  return (
    <HeroSection ref={sectionRef}>
      <OverscrollBleed aria-hidden="true" />
      <PhotoLayer aria-hidden="true">
        <HeroPhoto src={harmoniePhoto} alt="" />
        <PhotoVeil />
      </PhotoLayer>
      <SceneLayer>
        <SceneCanvas scene="ribbonveil" mode="scroll" scrollTarget={sectionRef} fallback={<Fallback />} />
      </SceneLayer>
      <Scrim />

      <HeroContent>
        <HeroBlock>
          <Eyebrow {...fade(reduced, 0.05)}>Anno 1831</Eyebrow>

          <GoldRule {...fade(reduced, 0.1)} aria-hidden="true" />

          <HeroTitle {...fade(reduced, 0.15)}>
            Koninklijke Poperingse Harmonie
            <br />
            <em>Sint-Cecilia</em>
          </HeroTitle>

          <HeroSubtitle {...fade(reduced, 0.3)}>
            Meer dan een eeuw passie voor harmoniemuziek
          </HeroSubtitle>

          <HeroButtons {...fade(reduced, 0.45)}>
            <Link to="/evenementen">
              <Button size="lg" variant="secondary">
                <Calendar size={20} />
                Onze Evenementen
              </Button>
            </Link>
            <Link to="/over-ons">
              <Button size="lg" variant="outline">
                Ontdek Ons Verhaal
              </Button>
            </Link>
          </HeroButtons>
        </HeroBlock>
      </HeroContent>
    </HeroSection>
  );
};
